import React from 'react';

// Shared logic for the dark multi-platform rating pills shown on
// posters (LoggedOutDiscover + ImageScroller). Keeps the two surfaces
// in sync so a tweak lands everywhere.

const PRIORITY_BY_CATEGORY = {
  1: ['imdb', 'kinopoisk', 'rottenTomatoes', 'metacritic'],
  2: ['openCritic', 'metacritic', 'steam', 'imdb'],
  3: ['goodreads', 'livelib', 'appleBooks', 'amazon'],
};
const PERCENT_PLATFORMS = new Set([
  'rottenTomatoes', 'rottenTomatoesAudience',
  'metacritic',
  'openCritic',
]);
const FIVE_SCALE_PLATFORMS = new Set(['goodreads', 'livelib', 'appleBooks', 'amazon', 'reddit']);

// metacriticUser / openCriticUser / rottenTomatoesAudience collapse
// onto their critic parent so one platform never shows twice. Any
// other name maps to itself.
const PLATFORM_FAMILY = {
  rottenTomatoesAudience: 'rottenTomatoes',
  metacriticUser: 'metacritic',
  openCriticUser: 'openCritic',
};
const familyOf = (name) => PLATFORM_FAMILY[name] || name;

const PLATFORM_ICON = {
  imdb: '/imdb_small_icon.png',
  kinopoisk: '/kinopoisk_small_icon.png',
  rottenTomatoes: '/tomato_icon.png',
  rottenTomatoesAudience: '/tomato_icon.png',
  metacritic: '/metacritic_icon.png',
  metacriticUser: '/metacritic_icon.png',
  openCritic: '/opencritic_icon.png',
  openCriticUser: '/opencritic_icon.png',
  steam: '/steam_icon.png',
  goodreads: '/goodreads_icon.png',
  livelib: '/livelib_icon.png',
  appleBooks: '/applebooks_icon.png',
  amazon: '/amazon_icon.png',
  reddit: '/reddit_icon.png',
};

// Score → 0–10 normalized. Caps at [0.1, 10] to drop backend outliers
// (e.g. openCriticUser occasionally stored /100 when its sibling is /10).
export const normalizeToTen = (name, score) => {
  if (score == null || score <= 0) return null;
  let n = score;
  if (PERCENT_PLATFORMS.has(name)) n = score / 10;
  else if (FIVE_SCALE_PLATFORMS.has(name)) n = score * 2;
  if (n < 0.1 || n > 10) return null;
  return n;
};

export const getTopRatings = (art, max = 3) => {
  const infos = art.otherPlatformInfos || [];
  const order = PRIORITY_BY_CATEGORY[art.category] || [];
  const seenFamilies = new Set();
  const out = [];
  const push = (name, score) => {
    if (!score || score <= 0) return;
    const fam = familyOf(name);
    if (seenFamilies.has(fam)) return;
    const n = normalizeToTen(name, score);
    if (n == null) return;
    seenFamilies.add(fam);
    out.push({ name, normalized: n });
  };
  for (const name of order) {
    if (out.length >= max) break;
    const f = infos.find((i) => i.name === name);
    if (f) push(name, f.score);
  }
  for (const i of infos) {
    if (out.length >= max) break;
    push(i.name, i.score);
  }
  return out;
};

// Vertical stack of dark corner pills with platform icon + /10 score.
// Positioned top-right of a `position: relative` poster parent.
export const RatingPillsStack = ({ art, max = 3 }) => {
  const ratings = getTopRatings(art, max);
  if (ratings.length === 0) return null;
  return (
    <div className="absolute flex flex-col gap-0.5" style={{ top: 6, right: 6 }}>
      {ratings.map((r) => {
        const icon = PLATFORM_ICON[r.name];
        return (
          <div
            key={r.name}
            className="flex items-center gap-0.5 px-1 py-0.5 rounded"
            style={{
              background: 'rgba(0,0,0,0.78)',
              color: 'white',
              backdropFilter: 'blur(2px)',
              WebkitBackdropFilter: 'blur(2px)',
              fontSize: 10,
              fontWeight: 600,
              lineHeight: 1,
            }}
            title={r.name}
          >
            {icon ? (
              <img
                src={icon}
                alt=""
                style={{ width: 12, height: 12, borderRadius: 2, objectFit: 'cover', display: 'block' }}
              />
            ) : (
              <span style={{ color: '#f5c518', fontSize: 10, lineHeight: 1 }}>★</span>
            )}
            <span style={{ lineHeight: 1 }}>{r.normalized.toFixed(1)}</span>
          </div>
        );
      })}
    </div>
  );
};
